/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

import { useAsyncTranslation } from '../hooks/useAutoTranslation';
import { usePageTranslationScanner } from '../hooks/usePageTranslationScanner';
import { useRecaptchaV3 } from '../components/CaptchaComponent';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

const DebateInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { executeRecaptcha } = useRecaptchaV3();
  
  // Enable translation scanning for this page
  usePageTranslationScanner(false);
  
  // Translation hooks
  const { translation: battleNamePlaceholder } = useAsyncTranslation("Enter a name that inspires fear in your opponent");
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [debate, setDebate] = useState<any>(null);
  const [teamName, setTeamName] = useState('');
  const [viewpoint, setViewpoint] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [, setPasswordError] = useState<string | null>(null);
  
  // Track authentication changes
  useEffect(() => {
    // If user becomes unauthenticated but there was an inviteCode,
    // store it for later use after login
    if (!isAuthenticated && inviteCode) {
      localStorage.setItem('pendingInvite', inviteCode);
    }
  }, [isAuthenticated, inviteCode]);
  
  // Debugging for the inviteCode parameter
  useEffect(() => {
    logger.info('Invite Code from URL:', inviteCode);
  }, [inviteCode]);
  
  // Fetch debate information by invite code
  useEffect(() => {
    const fetchDebate = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          makeApiPath(`/debates/invite/${inviteCode}`),
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
        );
        setDebate(response.data);
        
        // Pre-populate team name and viewpoint (for invited user/team 2)
        if (response.data.teams && response.data.teams.length > 1) {
          setTeamName(response.data.teams[1].name);
          setViewpoint(response.data.teams[1].viewpoint);
        }
      } catch (err) {
        logger.error('Error fetching debate:', err);
        setError(t('invalidOrExpiredInvitation'));
      } finally {
        setLoading(false);
      }
    };
    
    if (inviteCode && isAuthenticated) {
      fetchDebate();
    } else if (inviteCode && !isAuthenticated) {
      // If not authenticated, wait for login
      setLoading(false);
      setError(t('pleaseLoginToViewInvitation'));
    } else {
      setError(t('invalidInvitationLink'));
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t stable, omit to avoid re-fetch on t change
  }, [inviteCode, isAuthenticated]);
  
  // Handle joining the debate
  const handleJoinDebate = async () => {
    if (!isAuthenticated) {
      // Store the invite code in local storage so user can be redirected after login
      localStorage.setItem('pendingInvite', inviteCode || '');
      toast.error(t('pleaseLoginOrRegisterToJoin'));
      navigate('/login');
      return;
    }
    
    if (!teamName.trim()) {
      toast.error(t('pleaseEnterTeamName'));
      return;
    }
    
    try {
      setJoining(true);
      
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      // Create headers with Authorization token
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Execute reCAPTCHA to limit automated attempts
      const captchaToken = await executeRecaptcha('join_debate_invite');
      const response = await axios.post(
        makeApiPath(`/debates/join/${inviteCode}`), 
        {
          teamName,
          viewpoint,
          userId: user?.id,
          // Include password if debate requires one
          ...(debate?.invitePasswordEnabled ? { invitePassword } : {}),
          ...(captchaToken ? { captchaToken } : {})
        },
        { headers }
      );
      
      toast.success('Successfully joined the debate!');
      
      // Navigate to the debate room
      navigate(`/debate/${response.data.id}`);
    } catch (err: any) {
      logger.error('Error joining debate:', err);
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        setPasswordError(t('invitePasswordIncorrect', 'Incorrect password. Please try again.'));
        toast.error(t('invitePasswordIncorrect', 'Incorrect password. Please try again.'));
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        toast.error(t('tooManyAttempts', 'Too many attempts. Please try again later.'));
        setError(t('tooManyAttempts', 'Too many attempts. Please try again later.'));
      } else if (axios.isAxiosError(err) && err.response?.status === 400) {
        toast.error(t('teamFull', 'This team is already full.'));
        setError(t('teamFull', 'This team is already full.'));
      } else {
        toast.error(t('failedToJoinDebate', 'Failed to join debate'));
        setError(t('failedToJoinDebate', 'Failed to join debate. Please try again.'));
      }
    } finally {
      setJoining(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
        >
          {t('returnToHome')}
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          {t('theGauntletHasBeenThrown')}
        </Typography>
        
        <Alert severity="info" sx={{ my: 3 }}>
          <Typography variant="body1">
            {debate?.createdBy?.username || 'Someone'} {t('challengedYouToDebate')} <strong>{t('debateOfHonor')}</strong> {t('onTheTopic')}:
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
            {debate?.topic}
          </Typography>
          
          {/* Add public/private debate notice */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <strong>{t('debateType')}</strong>&nbsp;
              {debate?.isPublic ? (
                <span>
                  <strong>{t('publicDebates')}</strong> - {t('publicDebateNotice')}
                </span>
              ) : (
                <span>
                  <strong>{t('invitePagePrivateLabel')}</strong> - {t('privateDebateNotice')}
                </span>
              )}
            </Typography>
          </Box>
        </Alert>
        
        {debate?.description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('debate:description')}:
            </Typography>
            <Typography variant="body1">
              {debate.description}
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 3 }} />

        {/* Invite password field if required */}
        {debate?.invitePasswordEnabled && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('invitePasswordPrompt', 'This debate invite is password-protected. Enter the password provided by the challenger:')}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label={t('invitePasswordLabel', 'Invite Password')}
              value={invitePassword}
              onChange={(e) => setInvitePassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 2, bgcolor: 'white' }}
            />
          </Box>
        )}
        
        <Typography variant="h5" gutterBottom>
          {t('prepareYourDefense')}
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('invitePageImportantText')}</strong> {t('readCarefullyWarning')}
          </Typography>
        </Alert>
        
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'white' }}>
          <CardContent>
            {/* Show the creator's viewpoint (read-only) */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('opponentPositionReadonly')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={debate?.teams?.[0]?.viewpoint || ""}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              sx={{ mb: 3, bgcolor: '#f5f5f5' }}
            />
            
            {/* Editable fields for the opponent */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('yourPositionEditNeeded')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={viewpoint}
              onChange={(e) => setViewpoint(e.target.value)}
              variant="outlined"
              sx={{ mb: 2, bgcolor: 'white' }}
            />
            
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('yourBattleName')}
            </Typography>
            <TextField
              fullWidth
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder={battleNamePlaceholder}
              variant="outlined"
              sx={{ mb: 2, bgcolor: 'white' }}
            />
          </CardContent>
        </Card>
        
        {!isAuthenticated && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('youMustBeLoggedIn')}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
          >
            {t('declineChallenge')}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleJoinDebate}
            disabled={joining}
          >
            {joining ? <CircularProgress size={24} /> : t('acceptTheChallenge')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DebateInvitePage; 